import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { Pill } from '../ui/pill.js';
import {
  todayLocalIso,
  useDispatchBoard,
} from '../../lib/dispatch-board.js';

interface BoardDatePickerProps {
  /** Number of placed assignments on the currently-selected day. */
  placedOnDay: number;
  /** Number of placed assignments on OTHER days (off-screen on this view). */
  placedOnOtherDays: number;
}

/**
 * Day-of-week scrubber + native date input. Sits between the KpiStrip
 * and FilterBar inside the dispatch board card.
 *
 * Why prev/next + a native input rather than a custom calendar: the native
 * picker is keyboard-accessible everywhere, plays nicely with mobile
 * (iOS/Android wheel UIs), and we don't need range selection. Chevrons
 * cover the common single-day-step case so the user doesn't always have to
 * open the picker.
 */
export function BoardDatePicker({ placedOnDay, placedOnOtherDays }: BoardDatePickerProps) {
  const selectedDate = useDispatchBoard((s) => s.selectedDate);
  const setSelectedDate = useDispatchBoard((s) => s.setSelectedDate);
  const shiftSelectedDate = useDispatchBoard((s) => s.shiftSelectedDate);
  const goToToday = useDispatchBoard((s) => s.goToToday);

  const today = todayLocalIso();
  const isToday = selectedDate === today;
  const friendly = friendlyLabel(selectedDate, today);

  return (
    <div
      className="flex flex-wrap items-center gap-2 px-4 py-2 border-b"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
    >
      <button
        type="button"
        onClick={() => shiftSelectedDate(-1)}
        aria-label="Previous day"
        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-muted hover:bg-surface-muted hover:text-ink"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>

      <label className="relative inline-flex items-center">
        <Calendar
          className="h-4 w-4 absolute left-2.5 pointer-events-none"
          style={{ color: 'var(--color-text-tertiary)' }}
          aria-hidden
        />
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => {
            const next = e.target.value;
            if (next) setSelectedDate(next);
          }}
          className="h-8 pl-8 pr-2 rounded-md border bg-surface text-sm font-mono text-ink focus:outline-none focus:ring-2 focus:ring-brand-300"
          style={{ borderColor: 'var(--color-border-secondary)' }}
          aria-label="Board date"
        />
      </label>

      <button
        type="button"
        onClick={() => shiftSelectedDate(1)}
        aria-label="Next day"
        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-muted hover:bg-surface-muted hover:text-ink"
      >
        <ChevronRight className="h-4 w-4" />
      </button>

      <div className="flex items-center gap-2 min-w-0 flex-1">
        <span className="text-sm font-medium text-ink truncate">{friendly}</span>
        {!isToday && (
          <button
            type="button"
            onClick={goToToday}
            className="text-xs text-ink-accent hover:underline shrink-0"
          >
            Today
          </button>
        )}
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <Pill variant="info">
          {placedOnDay} placed
        </Pill>
        {placedOnOtherDays > 0 && (
          <Pill variant="neutral" title="Assignments scheduled for other days">
            +{placedOnOtherDays} other day{placedOnOtherDays === 1 ? '' : 's'}
          </Pill>
        )}
      </div>
    </div>
  );
}

function friendlyLabel(iso: string, todayIso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y!, (m ?? 1) - 1, d ?? 1);
  const weekday = dt.toLocaleDateString(undefined, { weekday: 'short' });
  const monthDay = dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  // Same-week relative labels — "Today" / "Tomorrow" / "Yesterday" — for
  // quick orientation. Beyond that, the absolute date carries enough.
  const relative = relativeLabel(iso, todayIso);
  if (relative) return `${relative} · ${weekday} ${monthDay}`;
  return `${weekday}, ${monthDay}, ${dt.getFullYear()}`;
}

function relativeLabel(iso: string, todayIso: string): string | null {
  if (iso === todayIso) return 'Today';
  const [ty, tm, td] = todayIso.split('-').map(Number);
  const [y, m, d] = iso.split('-').map(Number);
  const today = new Date(ty!, (tm ?? 1) - 1, td ?? 1);
  const target = new Date(y!, (m ?? 1) - 1, d ?? 1);
  const diffDays = Math.round((target.getTime() - today.getTime()) / 86_400_000);
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';
  return null;
}
